import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";

import { selectIdentity } from "../../../selectors/userSelectors";
import Spinner from "../../../shared/ui/Loading/Spinner";
import { messageFromError } from "../../../shared/api/drfErrors";
import { fetchMyCompany } from "../../companyWorkspace/api/companyWorkspace";
import {
  isAdminMembership,
  isActiveMembership,
} from "../../companyWorkspace/lib/companyLabels";
import {
  createScopeGate,
  sameActorId,
  sameCompanyId,
} from "../../companyWorkspace/lib/scopeSession";
import {
  FOCUS_REVALIDATION_UNRESOLVED_MESSAGE,
  openBackgroundRevalidation,
  settleRevalidationWrites,
  useCompanyFocusRevalidation,
} from "../../companyWorkspace/lib/companyFocusRevalidation";

import { previewClientTransfer } from "../api/companyClients";
import { isCorporateClientsAvailable } from "../lib/clientLabels";
import { blockerLabel, relationEntries } from "../lib/transferBlockers";

function sameGeneration(a, b) {
  if (a == null && b == null) return true;
  if (a == null || b == null) return false;
  return String(a) === String(b);
}

function sameClientId(a, b) {
  if (a == null || b == null) return false;
  return String(a) === String(b);
}

function authorityContextEqual(a, b) {
  if (!a || !b) return false;
  return (
    sameActorId(a.actorId, b.actorId) &&
    sameCompanyId(a.companyId, b.companyId) &&
    sameClientId(a.clientId, b.clientId) &&
    sameGeneration(a.membershipGeneration, b.membershipGeneration)
  );
}

/**
 * Personal Client detail affordance: personal owner + OWNER/ADMIN company.
 * Preview-only — never offers apply. apply_available is always false from C1.
 * Preview results stay bound to live actor/company/client/membership_generation.
 */
function PersonalTransferPreviewCard({ clientId, isOwner = false }) {
  const identity = useSelector(selectIdentity);
  const actorId = identity?.userId ?? null;
  const scopeGate = useRef(createScopeGate()).current;
  const mountedRef = useRef(true);
  const busyOpRef = useRef(null);
  const liveAuthorityRef = useRef({
    actorId: null,
    companyId: null,
    clientId: null,
    membershipGeneration: null,
  });

  const [eligible, setEligible] = useState(false);
  const [companyId, setCompanyId] = useState(null);
  const [companyName, setCompanyName] = useState("");
  const [membershipGeneration, setMembershipGeneration] = useState(null);
  const [authorityActorId, setAuthorityActorId] = useState(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [writesSuspended, setWritesSuspended] = useState(false);
  const writesSuspendedRef = useRef(false);
  const revalidationTokenRef = useRef(0);
  const [checkedAuth, setCheckedAuth] = useState(false);
  const checkedAuthRef = useRef(false);
  checkedAuthRef.current = checkedAuth;
  writesSuspendedRef.current = writesSuspended;

  const clearPreviewState = useCallback(() => {
    setPreview(null);
    setError(null);
  }, []);

  const clearAuthority = useCallback(() => {
    liveAuthorityRef.current = {
      actorId: null,
      companyId: null,
      clientId: null,
      membershipGeneration: null,
    };
    setEligible(false);
    setCompanyId(null);
    setCompanyName("");
    setMembershipGeneration(null);
    setAuthorityActorId(null);
    clearPreviewState();
  }, [clearPreviewState]);

  const refreshAuthority = useCallback(async ({ background = false } = {}) => {
    if (!isOwner || !clientId) {
      clearAuthority();
      setCheckedAuth(true);
      return;
    }
    const boundCompanyId = liveAuthorityRef.current.companyId;
    scopeGate.bindIdentity({
      actorId,
      companyId: boundCompanyId,
    });
    const op = scopeGate.beginRead({
      actorId,
      companyId: boundCompanyId,
      resourceId: clientId,
    });
    const keepVisible = background && checkedAuthRef.current;
    const tokenAtStart = revalidationTokenRef.current;
    let backgroundToken = null;
    let revalidationConfirmed = false;
    if (keepVisible) {
      backgroundToken = openBackgroundRevalidation(
        revalidationTokenRef,
        writesSuspendedRef,
        setWritesSuspended,
      );
    } else {
      setCheckedAuth(false);
    }
    try {
      const mine = await fetchMyCompany();
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      const company = mine?.company || null;
      const membership = mine?.membership || null;
      const ok =
        isActiveMembership(company, membership) &&
        isAdminMembership(company, membership) &&
        isCorporateClientsAvailable(company);
      if (!ok || company?.id == null) {
        clearAuthority();
        setCheckedAuth(true);
        return;
      }

      const nextContext = {
        actorId,
        companyId: company.id,
        clientId,
        membershipGeneration:
          membership?.membership_generation != null
            ? membership.membership_generation
            : null,
      };
      if (!authorityContextEqual(liveAuthorityRef.current, nextContext)) {
        clearPreviewState();
      }
      liveAuthorityRef.current = nextContext;
      scopeGate.bindIdentity({
        actorId: nextContext.actorId,
        companyId: nextContext.companyId,
      });
      setEligible(true);
      setCompanyId(nextContext.companyId);
      setCompanyName(company.name || "");
      setMembershipGeneration(nextContext.membershipGeneration);
      setAuthorityActorId(actorId);
      setCheckedAuth(true);
      revalidationConfirmed = true;
      setError((current) =>
        current === FOCUS_REVALIDATION_UNRESOLVED_MESSAGE ? null : current,
      );
    } catch (err) {
      if (!scopeGate.readStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      const status = err?.response?.status;
      if (keepVisible && status !== 403 && status !== 404) {
        setError(FOCUS_REVALIDATION_UNRESOLVED_MESSAGE);
        return;
      }
      clearAuthority();
      setCheckedAuth(true);
    } finally {
      settleRevalidationWrites({
        keepVisible,
        confirmed: revalidationConfirmed,
        token: backgroundToken,
        tokenAtStart,
        tokenRef: revalidationTokenRef,
        writesSuspendedRef,
        setWritesSuspended,
      });
    }
  }, [
    actorId,
    clearAuthority,
    clearPreviewState,
    clientId,
    isOwner,
    scopeGate,
  ]);

  useEffect(() => {
    mountedRef.current = true;
    scopeGate.bindIdentity({ actorId, companyId: null });
    busyOpRef.current = null;
    setBusy(false);
    clearPreviewState();
    liveAuthorityRef.current = {
      actorId: null,
      companyId: null,
      clientId: null,
      membershipGeneration: null,
    };
    refreshAuthority();
    return () => {
      mountedRef.current = false;
      scopeGate.invalidate();
      busyOpRef.current = null;
    };
  }, [actorId, clientId, isOwner, refreshAuthority, scopeGate, clearPreviewState]);

  useCompanyFocusRevalidation({
    enabled: Boolean(isOwner && clientId),
    busy,
    onRevalidate: () => refreshAuthority({ background: true }),
  });

  const actorMatches =
    authorityActorId != null && sameActorId(authorityActorId, actorId);
  const previewContextLive = authorityContextEqual(liveAuthorityRef.current, {
    actorId,
    companyId,
    clientId,
    membershipGeneration,
  });

  if (
    !isOwner ||
    !checkedAuth ||
    !actorMatches ||
    !eligible ||
    !companyId ||
    !previewContextLive
  ) {
    return null;
  }

  const handlePreview = async () => {
    if (busyOpRef.current || writesSuspendedRef.current) return;
    const captured = { ...liveAuthorityRef.current };
    if (
      !sameCompanyId(captured.companyId, companyId) ||
      !sameClientId(captured.clientId, clientId) ||
      !sameActorId(captured.actorId, actorId)
    ) {
      return;
    }
    const op = scopeGate.beginMutation({
      actorId: captured.actorId,
      companyId: captured.companyId,
      resourceId: captured.clientId,
    });
    busyOpRef.current = op;
    setBusy(true);
    setError(null);
    setPreview(null);
    try {
      const data = await previewClientTransfer(
        captured.companyId,
        captured.clientId,
      );
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      if (!authorityContextEqual(liveAuthorityRef.current, captured)) {
        return;
      }
      setPreview(data);
    } catch (err) {
      if (!scopeGate.mutationStillValid(op, { mounted: mountedRef.current })) {
        return;
      }
      if (!authorityContextEqual(liveAuthorityRef.current, captured)) {
        return;
      }
      const status = err?.response?.status;
      if (status === 403 || status === 404) {
        clearAuthority();
        setCheckedAuth(true);
        return;
      }
      setError(messageFromError(err));
    } finally {
      if (busyOpRef.current === op) {
        busyOpRef.current = null;
        setBusy(false);
      }
    }
  };

  const blockers = Array.isArray(preview?.blockers) ? preview.blockers : [];
  const relations = relationEntries(preview?.relations);
  const applyAvailable = preview?.apply_available === true;

  return (
    <section
      className="feature-company-workspace__card feature-company-clients__transfer"
      data-testid="personal-transfer-preview"
      data-company-id={String(companyId)}
      data-membership-generation={
        membershipGeneration == null ? "" : String(membershipGeneration)
      }
    >
      <h2>Перенесення до компанії</h2>
      <p>
        Перевірка можливості перенести цей особистий запис до «{companyName}».
        Застосування недоступне — лише попередній перегляд.
      </p>
      <button
        type="button"
        className="action-btn"
        onClick={handlePreview}
        disabled={busy || writesSuspended}
      >
        {busy ? "Перевірка…" : "Перевірити перенесення до компанії"}
      </button>
      {busy ? <Spinner /> : null}
      {error ? (
        <p className="feature-company-workspace__error" role="alert">
          {error}
        </p>
      ) : null}
      {preview ? (
        <div data-testid="personal-transfer-preview-result">
          <p>
            {preview.ready
              ? "Поточний перегляд не знайшов перешкод (ready)."
              : "Є перешкоди для перенесення."}{" "}
            Застосування недоступне.
          </p>
          {applyAvailable ? (
            <p className="feature-company-workspace__error">
              Неочікуваний сигнал apply_available — кнопку застосування не
              показано.
            </p>
          ) : null}
          {relations.length > 0 ? (
            <ul aria-label="Зв'язки клієнта">
              {relations.map((r) => (
                <li key={r.key}>
                  {r.label}: {r.summary}
                </li>
              ))}
            </ul>
          ) : null}
          {blockers.length > 0 ? (
            <ul aria-label="Перешкоди перенесення">
              {blockers.map((b, idx) => (
                <li key={`${b.code || "x"}-${idx}`}>
                  {blockerLabel(b.code)}
                  {typeof b.count === "number" ? ` (${b.count})` : ""}
                </li>
              ))}
            </ul>
          ) : null}
          {/* Explicitly no apply / confirm control — C1 apply_available:false */}
        </div>
      ) : null}
    </section>
  );
}

export default PersonalTransferPreviewCard;
